import {
  type ImageBlockProps,
  ImageBlockSchema,
} from '@blocksuite/affine-model';
import {
  FetchUtils,
  FULL_FILE_PATH_KEY,
  getImageFullPath,
} from '@blocksuite/affine-shared/adapters';
import {
  getFilenameFromContentDisposition,
  inferMimeTypeFromName,
  readImageSize,
  resolveBlobDisplayName,
} from '@blocksuite/affine-shared/utils';
import {
  type AssetsManager,
  type ASTWalkerContext,
  type BlockSnapshot,
  nanoid,
} from '@blocksuite/store';

function imageUrlName(imageURL: string): string {
  const rawPathName =
    (imageURL.split(/[\\/]/).at(-1) ?? '').split(/[?#]/, 1)[0] ?? '';
  try {
    return decodeURIComponent(rawPathName).trim();
  } catch {
    return rawPathName.trim();
  }
}

async function readImportedImageLayoutProps(
  blob: Blob | undefined
): Promise<
  Partial<Pick<ImageBlockProps, 'height' | 'name' | 'size' | 'type' | 'width'>>
> {
  if (!blob) return {};

  const props: Partial<
    Pick<ImageBlockProps, 'height' | 'name' | 'size' | 'type' | 'width'>
  > = {
    ...(blob instanceof File && blob.name ? { name: blob.name } : {}),
    size: blob.size,
    type: blob.type,
  };

  try {
    const { width, height } = await readImageSize(blob);
    if (width > 0 && height > 0) {
      props.width = width;
      props.height = height;
    }
  } catch {
    // Image metadata improves layout stability but must not block import.
  }

  return props;
}

export async function processImageNodeToBlock(
  imageURL: string,
  walkerContext: ASTWalkerContext<BlockSnapshot>,
  assets: AssetsManager,
  configs: Map<string, string>
) {
  let blobId = '';
  if (!FetchUtils.fetchable(imageURL)) {
    const fullFilePath = configs.get(FULL_FILE_PATH_KEY);
    // When importing markdown file with assets in a zip file,
    // the image URL is the relative path of the image file in the zip file
    // If the full file path is provided, use it to get the image full path
    if (fullFilePath) {
      const decodedImageURL = decodeURIComponent(imageURL);
      const imageFullPath = getImageFullPath(fullFilePath, decodedImageURL);
      blobId = assets.getPathBlobIdMap().get(imageFullPath) ?? '';
    } else {
      const imageURLSplit = imageURL.split('/');
      while (imageURLSplit.length > 0) {
        const key = assets
          .getPathBlobIdMap()
          .get(decodeURIComponent(imageURLSplit.join('/')));
        if (key) {
          blobId = key;
          break;
        }
        imageURLSplit.shift();
      }
    }
  } else {
    try {
      const res = await FetchUtils.fetchImage(
        imageURL,
        undefined,
        configs.get('imageProxy') as string
      );
      if (!res) {
        return;
      }
      const responseMime = res.headers.get('Content-Type')?.trim() ?? '';
      const occurrenceName =
        getFilenameFromContentDisposition(
          res.headers.get('Content-Disposition') ?? ''
        ) ?? imageUrlName(imageURL);
      const type = responseMime.startsWith('image/')
        ? responseMime
        : (inferMimeTypeFromName(occurrenceName) ?? responseMime);
      const name = resolveBlobDisplayName({
        occurrenceName,
        fallbackName: 'image',
        mimeType: type,
      });
      const file = new File([await res.blob()], name, {
        type,
      });
      blobId = crypto.randomUUID();
      assets?.getAssets().set(blobId, file);
      await assets?.writeToBlob(blobId);
    } catch (err) {
      console.error('Failed to process image:', err);
      return;
    }
  }
  if (!blobId) {
    return;
  }
  const imageLayoutProps = await readImportedImageLayoutProps(
    assets.getAssets().get(blobId)
  );
  if (!imageLayoutProps.name) {
    const pathName = imageUrlName(imageURL);
    if (pathName) imageLayoutProps.name = pathName;
  }
  if (!imageLayoutProps.type?.startsWith('image/')) {
    imageLayoutProps.type =
      inferMimeTypeFromName(imageLayoutProps.name) ?? imageLayoutProps.type;
  }
  walkerContext
    .openNode(
      {
        type: 'block',
        id: nanoid(),
        flavour: ImageBlockSchema.model.flavour,
        props: {
          sourceId: blobId,
          ...imageLayoutProps,
        },
        children: [],
      },
      'children'
    )
    .closeNode();
  walkerContext.skipAllChildren();
}
